import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Instagram, Mail } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useStoreSettings, getLocalizedText } from '../../contexts/StoreSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes pour les réseaux sociaux
function SocialIcon({ platform }) {
  switch (platform) {
    case 'instagram':
      return <Instagram className="h-5 w-5" />;
    case 'facebook':
      return <Facebook className="h-5 w-5" />;
    case 'tiktok':
      return (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      );
    default:
      return null;
  }
}

export function Footer() {
  const { t, i18n } = useTranslation();
  const { settings } = useStoreSettings();
  const lang = i18n.language;

  const { brand, footer, header } = settings;
  const storeName = getLocalizedText(brand?.store_name, lang);
  const slogan = getLocalizedText(brand?.slogan, lang);
  const copyrightText = getLocalizedText(footer?.copyright_text, lang);
  const newsletterTitle = getLocalizedText(footer?.newsletter_title, lang);
  const newsletterPlaceholder = getLocalizedText(footer?.newsletter_placeholder, lang);
  
  const columns = footer?.columns || [];
  const socialLinks = header?.social_links?.filter(s => s.enabled && s.url) || [];

  // Rendu du logo
  const renderLogo = () => {
    if (brand?.logo_light) {
      const logoSrc = brand.logo_light.startsWith('/api/') 
        ? `${API_URL}${brand.logo_light}`
        : brand.logo_light;
      return (
        <img 
          src={logoSrc} 
          alt={storeName}
          className="h-8 object-contain"
        />
      );
    }
    // Logo image par défaut
    return (
      <img 
        src="/logo.png" 
        alt="Mutagim.shop"
        className="h-8 object-contain"
      />
    );
  };

  return (
    <footer className="bg-mutagim-dark border-t border-gray-800" data-testid="footer">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(columns.length + 2, 5)} gap-8`}>
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              {renderLogo()}
            </Link>
            <p className="text-gray-400 text-sm">
              {slogan}
            </p>
            {/* Social links */}
            {socialLinks.length > 0 && (
              <div className="flex gap-4">
                {socialLinks.map(social => (
                  <a 
                    key={social.platform}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-mutagim-red transition-colors" 
                    aria-label={social.platform}
                  >
                    <SocialIcon platform={social.platform} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Dynamic Columns */}
          {columns.map(column => (
            <div key={column.id}>
              <h3 className="text-white font-semibold mb-4">
                {getLocalizedText(column.title, lang)}
              </h3>
              <ul className="space-y-2">
                {column.links?.filter(l => l.enabled !== false).map(link => (
                  <li key={link.id}>
                    <Link 
                      to={link.url}
                      target={link.target || '_self'}
                      className="text-gray-400 hover:text-mutagim-red text-sm transition-colors"
                    >
                      {getLocalizedText(link.label, lang)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Legal - Toujours présent */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.aboutUs', 'À propos')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/notre-systeme" className="text-gray-400 hover:text-mutagim-red text-sm transition-colors" data-testid="footer-notre-systeme">
                  {t('footer.ourSystem', 'Notre Système')}
                </Link>
              </li>
              <li>
                <Link to="/cgv" className="text-gray-400 hover:text-mutagim-red text-sm transition-colors" data-testid="footer-cgv">
                  {t('legal.cgv')}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-400 hover:text-mutagim-red text-sm transition-colors" data-testid="footer-privacy">
                  {t('legal.privacy')}
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-gray-400 hover:text-mutagim-red text-sm transition-colors" data-testid="footer-cookies">
                  {t('legal.cookiesPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/mentions-legales" className="text-gray-400 hover:text-mutagim-red text-sm transition-colors" data-testid="footer-legal-notice">
                  {t('legal.legalNotice')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          {footer?.show_newsletter !== false && (
            <div>
              <h3 className="text-white font-semibold mb-4">
                {newsletterTitle || t('footer.newsletter')}
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                {t('footer.newsletterDescription', 'Inscrivez-vous pour recevoir nos offres exclusives')}
              </p>
              <form className="space-y-2" onSubmit={(e) => e.preventDefault()}>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder={newsletterPlaceholder || t('footer.newsletterPlaceholder')}
                    className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-400"
                    data-testid="newsletter-input"
                  />
                  <Button 
                    type="submit" 
                    className="bg-mutagim-red hover:bg-red-700 text-white"
                    data-testid="newsletter-submit"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              {copyrightText || `© ${new Date().getFullYear()} ${storeName}. ${t('footer.allRights')}.`}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <Link to="/cgv" className="text-gray-500 hover:text-mutagim-red transition-colors" data-testid="footer-terms">
                {t('footer.terms')}
              </Link>
              <Link to="/privacy" className="text-gray-500 hover:text-mutagim-red transition-colors">
                {t('footer.privacy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
